'use client';

import Image from 'next/image';

import {
  useDeviceImageUploadMutation,
  TAssessmentImageType,
  TImageMetaPositionAlt,
} from '@northladder/apis';
import { ButtonV1, LoaderRetailApp } from '@northladder/design-system';
import { Text } from '@northladder/i18n';
import { useRegionStore } from '@northladder/services';
import {
  dataURLtoFile,
  ErrorEventsEnum,
  errorLogToRemoteUtil,
  IApiError,
  TTextLocalized,
} from '@northladder/utilities';

import { translations } from '../../../translations';

import { useRouteURL, useTargetAssessmentConfig } from '../../hooks';
import { IntroAssessBlackOptions } from '../../types';

interface IImageUploadingProps {
  assessmentImageType: TAssessmentImageType | TImageMetaPositionAlt;
  imageSrc: string;
}

function ImageUploading({
  assessmentImageType,
  imageSrc,
}: IImageUploadingProps) {
  const { previewAndUpload } = useTargetAssessmentConfig(assessmentImageType);

  return (
    <div className="mx-auto flex w-auto min-w-[300px] flex-col justify-center">
      <div className="mx-auto flex max-w-sm flex-col items-center">
        <div className="mx-auto mb-4 flex max-w-sm flex-col items-center  md:max-w-2xl">
          <h1
            className="
              text-md text-primary-700 dark:text-primary-400 px-20 font-normal
              tracking-tight
            "
          >
            <Text text={translations.uploadModalContent.uploadImageAlt} />
          </h1>
          <p
            className="
              text-primary-900 dark:text-primary-200 w-2/3 max-w-xl text-sm
              font-light
            "
          >
            <Text text={previewAndUpload.description} />
          </p>
        </div>
      </div>
      <div
        className="
          bg-primary-100 relative mx-auto flex max-w-sm flex-col
          items-center shadow-inner
        "
      >
        <Image
          alt="Captured image preview"
          className="my-4 h-[20rem] w-full object-contain object-center opacity-20"
          height={1200}
          src={imageSrc}
          width={600}
        />
        <LoaderRetailApp className="absolute bottom-0 left-0 right-0 top-0" />
      </div>

      <div className="mt-4 py-2">
        <p className="flex-start flex flex-col">
          <span className="text-primary-700 dark:text-primary-400 mx-1 text-sm">
            <Text text={translations.uploadModalContent.pleaseWait} />
          </span>
        </p>
      </div>

      <div className="flex w-full justify-between gap-10">
        <ButtonV1
          className="form-btn-secondary my-4 border border-gray-300 text-gray-700"
          isEnabled={false}
          label={translations.uploadModalContent.retake}
          onClick={() => null}
          type="button"
        />
        <ButtonV1
          className="my-4"
          isEnabled={false}
          label={translations.uploadModalContent.uploading}
          onClick={() => null}
          type="button"
        />
      </div>
    </div>
  );
}

interface IImageUploadFailedFallbackProps {
  assessmentImageType: TAssessmentImageType | TImageMetaPositionAlt;
  error: {
    message: string;
    description?: TTextLocalized;
  };
  imageSrc: string;
  onContinueAfterFail: () => void;
  onRetake: () => void;
  onRestart: () => void;
  onRetryUpload: () => void;
}

function ImageUploadFailedFallback({
  assessmentImageType,
  error,
  imageSrc,
  onContinueAfterFail,
  onRetake,
  onRestart,
  onRetryUpload,
}: IImageUploadFailedFallbackProps) {
  const { previewAndUpload } = useTargetAssessmentConfig(assessmentImageType);

  // TODO: Get unique flags for image upload errors for the back end error
  // instead of doing these checks.
  const isAlreadyUploaded =
    typeof error?.message === 'string' &&
    error.message.includes('already uploaded');
  const isSessionExpired =
    typeof error.message === 'string' &&
    error.message.includes('Session Expired');

  let errorMessage = isAlreadyUploaded
    ? 'The image for this side of the phone has already been uploaded.'
    : error?.message;

  let errorDescription = isAlreadyUploaded
    ? 'Please either continue or restart the assessment.'
    : error?.description;

  if (isSessionExpired) {
    errorMessage = 'This session has expired';
    errorDescription =
      'Please restart the assessment by scanning a new QR code.';
  }

  return (
    <div className="mx-auto flex w-auto min-w-[300px] flex-col justify-center">
      <div className="mx-auto flex max-w-sm flex-col items-center">
        <div className="mx-auto mb-4 flex max-w-sm flex-col items-center  md:max-w-2xl">
          <h1 className="text-md font-normal tracking-tight text-red-700">
            <Text text={translations.uploadModalContent.failedToUpload} />
          </h1>
          <p
            className="
              text-primary-900 dark:text-primary-200 w-2/3 max-w-xl text-sm
              font-light
            "
          >
            <Text isInjectableHtml text={previewAndUpload.description} />
          </p>
        </div>
      </div>
      <div
        className="
          mx-auto flex max-w-sm flex-col items-center bg-red-50
          shadow-inner
        "
      >
        <Image
          alt="Captured image preview"
          className="my-4 h-[20rem] w-full object-contain object-center"
          height={1200}
          src={imageSrc}
          width={600}
        />
      </div>
      <div className="mt-4 py-2">
        <p className="flex-start flex flex-col">
          <span className="mx-1 text-sm text-red-700">
            <Text text={errorMessage} />
          </span>

          {errorDescription ? (
            <span className="mx-1 my-2 text-xs text-red-900">
              <Text text={errorDescription} />
            </span>
          ) : null}
        </p>
      </div>

      <div className="flex w-full justify-between gap-10">
        {/* eslint-disable-next-line no-nested-ternary */}
        {isAlreadyUploaded ? (
          <ButtonV1
            className="my-4 sm:mx-auto sm:w-auto sm:px-24"
            label={translations.uploadModalContent.continue}
            onClick={onContinueAfterFail}
            type="button"
          />
        ) : isSessionExpired ? null : (
          <>
            <ButtonV1
              className="form-btn-secondary my-4 border border-gray-300 text-gray-700"
              label={translations.uploadModalContent.retake}
              onClick={onRetake}
              type="button"
            />
            <ButtonV1
              className="my-4"
              label={translations.uploadModalContent.retry}
              onClick={onRetryUpload}
              type="button"
            />
          </>
        )}
      </div>

      <div className="py-4 text-sm font-light text-gray-500 dark:text-gray-400">
        <Text text={translations.uploadModalContent.beginAfresh} />
        <button
          className="form-link-action ms-2"
          onClick={onRestart}
          type="button"
        >
          <Text text={translations.uploadModalContent.restartAssessment} />
        </button>
      </div>
    </div>
  );
}

interface IImagePreviewProps {
  assessmentImageType: TAssessmentImageType | TImageMetaPositionAlt;
  imageSrc: string;
  onRetake: () => void;
  onUpload: () => void;
}

function ImagePreview({
  assessmentImageType,
  imageSrc,
  onRetake,
  onUpload,
}: IImagePreviewProps) {
  const { previewAndUpload } = useTargetAssessmentConfig(assessmentImageType);
  const { captureSuccess, description } = previewAndUpload;

  return (
    <div>
      <div className="mx-auto flex max-w-sm flex-col items-center">
        <div className="mx-auto mb-4 flex max-w-sm flex-col items-center md:max-w-2xl">
          <h1
            className="
              text-md text-primary-700 dark:text-primary-400 font-normal tracking-tight
            "
          >
            <Text isInjectableHtml text={captureSuccess.title} />
          </h1>
          {description ? (
            <p
              className="
                text-primary-900 dark:text-primary-200 w-2/3 max-w-xl text-sm
                font-light
              "
            >
              <Text isInjectableHtml text={description} />
            </p>
          ) : null}
        </div>
      </div>
      <div
        className="
          mx-auto flex max-w-sm flex-col items-center
        "
      >
        <Image
          alt="Captured image preview"
          className="my-4 h-[30rem] w-full object-contain object-center"
          height={1200}
          src={imageSrc}
          width={600}
        />
      </div>
      <div className="flex w-full justify-between gap-10">
        <ButtonV1
          className="form-btn-secondary my-4 border border-gray-300 text-gray-700"
          label={translations.imagePreview.retake}
          onClick={onRetake}
          type="button"
        />
        <ButtonV1
          className="my-4"
          label={translations.imagePreview.confirm}
          onClick={onUpload}
          type="button"
        />
      </div>
    </div>
  );
}

interface IPreviewAndUploadProps extends IntroAssessBlackOptions {
  assessmentImageType: TAssessmentImageType | TImageMetaPositionAlt;
  imageSrc: string;
  identifier: string;
  onNextAssessment: () => void;
  onRestart: () => void;
}

export function PreviewAndUpload({
  assessmentImageType,
  identifier = '',
  imageSrc,
  onGoTo,
  onNextAssessment,
  onRestart,
}: IPreviewAndUploadProps) {
  const { sessionId, assessmentType } = useRouteURL();
  const baseURL = useRegionStore().tenantConfig?.apiBaseURL || '';

  const handleClickRestart = () => {
    onRestart();
  };

  const handleClickRetake = () => {
    onGoTo('4_IMAGE_CAPTURE');
  };

  const handleUploadSuccess = () => {
    onGoTo('6_UPLOAD_SUCCESS');
  };

  const handleOnContinueAfterFail = () => {
    onNextAssessment();
  };

  const handleUploadFailed = (error: IApiError) => {
    errorLogToRemoteUtil({
      error,
      errorCode: ErrorEventsEnum.ERROR_IN_API_CALL,
      errorTitle: 'Error in Uploading Cosmetic Diag Image',
      message: error.message,
    });
  };

  const {
    error,
    isPending: isUploadingImage,
    uploadImage,
  } = useDeviceImageUploadMutation({
    onSuccessCallback: handleUploadSuccess,
    onErrorCallback: handleUploadFailed,
  });

  const handleUploadImage = async () => {
    const imageFormData = new FormData();
    const time = new Date().getTime().toString();
    const timeStamp = time.slice(-4);

    imageFormData.append('image', imageSrc);

    const imageFile = dataURLtoFile(
      imageSrc,
      `${assessmentImageType}_${sessionId}_${timeStamp}.jpg`
    );

    // Note: Currently, the apple flow uses a different identifier for the image
    // which is not the case with the GRADE_PRO flow.
    const imageIdentifier =
      assessmentType === 'APPLE' ? assessmentImageType : identifier;

    uploadImage({
      assessmentType,
      baseURL,
      file: imageFile,
      id: sessionId,
      imageId: imageIdentifier,
    });
  };

  return (
    <main className="flex min-h-[calc(100vh-230px)] items-center justify-center">
      <div
        className="
          mx-auto my-auto max-w-4xl px-4 pb-8 text-center sm:px-6 lg:px-8
        "
      >
        {isUploadingImage ? (
          <ImageUploading
            assessmentImageType={assessmentImageType}
            imageSrc={imageSrc}
          />
        ) : null}

        {error ? (
          <ImageUploadFailedFallback
            assessmentImageType={assessmentImageType}
            error={{
              message: error.message as string,
              description: error.description,
            }}
            imageSrc={imageSrc}
            onContinueAfterFail={handleOnContinueAfterFail}
            onRestart={handleClickRestart}
            onRetake={handleClickRetake}
            onRetryUpload={handleUploadImage}
          />
        ) : null}

        {!isUploadingImage && !error ? (
          <ImagePreview
            assessmentImageType={assessmentImageType}
            imageSrc={imageSrc}
            onRetake={handleClickRetake}
            onUpload={handleUploadImage}
          />
        ) : null}
      </div>
    </main>
  );
}
